import React from 'react'
import { LucideIcon } from 'lucide-react'
import { UIButton, type UIButtonProps } from './ui-button'
import { cn } from '../../lib/utils'

export interface UIButtonIconProps extends
  Omit<UIButtonProps, 'size' | 'children'> {
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  children?: React.ReactNode
  size?: 'sm' | 'default' | 'lg' | 'icon'
  /**
   * Accessible label for the button. Required when using icon-only mode (no children).
   * Describes what the button does for screen readers.
   */
  'aria-label'?: string
}

/**
 * Enhanced Icon Button Component
 * Supports both icon-only and icon-with-text patterns
 * Based on shadcn button-05 block with comprehensive prop filtering
 */
export const UIButtonIcon = React.forwardRef<HTMLButtonElement, UIButtonIconProps>(
  ({ icon: Icon, iconPosition = 'left', children, size = 'icon', variant = "outline", className, 'aria-label': ariaLabel, ...cleanProps }, ref) => {
    // Development warning for accessibility
    React.useEffect(() => {
      if (!children && Icon && !ariaLabel && process.env.NODE_ENV === 'development') {
        console.warn(
          'UIButtonIcon: Icon-only button missing aria-label. ' +
          'Please provide an aria-label prop to describe what this button does for screen readers.\n' +
          'Example: <UIButtonIcon icon={Settings} aria-label="Open settings" />'
        )
      }
    }, [children, Icon, ariaLabel])

    // Icon-only mode
    if (!children && Icon) {
      return (
        <UIButton
          size="icon"
          variant={variant}
          className={cn(className)}
          aria-label={ariaLabel}
          ref={ref}
          {...cleanProps}
        >
          <Icon className="h-4 w-4" />
        </UIButton>
      )
    }

    // Icon with text mode
    if (children && Icon) {
      return (
        <UIButton
          size={size === 'icon' ? 'default' : size}
          variant={variant}
          className={cn(className)}
          aria-label={ariaLabel}
          ref={ref}
          {...cleanProps}
        >
          {iconPosition === 'left' && <Icon className="mr-2 h-4 w-4" />}
          {children}
          {iconPosition === 'right' && <Icon className="ml-2 h-4 w-4" />}
        </UIButton>
      )
    }

    // Fallback: children-only mode (backwards compatibility)
    return (
      <UIButton
        size={size === 'icon' ? 'icon' : size}
        variant={variant}
        className={cn(className)}
        aria-label={ariaLabel}
        ref={ref}
        {...cleanProps}
      >
        {children}
      </UIButton>
    )
  }
)

UIButtonIcon.displayName = "UIButtonIcon"